import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Target, 
  TrendingUp,
  BookOpen,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Filter,
  Search,
  BarChart3,
  Award,
  Zap,
  Calendar,
  Users,
  Trophy,
  Star,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { QuestionComponent } from './QuestionTypes';
import KatexRenderer from './KatexRenderer';

const TestAnalysisModal = ({ 
  isOpen, 
  onClose, 
  testResult, 
  testData = null 
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showOnlyIncorrect, setShowOnlyIncorrect] = useState(false);
  const [showOnlyUnattempted, setShowOnlyUnattempted] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const [showExplanations] = useState(true);
  const [filterByDifficulty, setFilterByDifficulty] = useState('all');
  const [filterByTopic, setFilterByTopic] = useState('all');
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [isPerformanceOverviewExpanded, setIsPerformanceOverviewExpanded] = useState(false);

  // Extract data from test result
  const {
    questions = [],
    answers = {},
    detailedResults = [],
    score = 0,
    totalMarks = 100,
    correctAnswers = 0,
    wrongAnswers = 0,
    unattempted = 0,
    accuracy = 0,
    timeSpent = 0,
    testName = '',
    completedAt = new Date(),
    sectionalResults = []
  } = testResult || {};

  // Calculate derived values
  const totalQuestions = questions.length;
  const percentageScore = totalMarks > 0 ? Math.round((score / totalMarks) * 100) : 0;

  // Filter questions based on current filters
  const getFilteredQuestions = () => {
    let filtered = questions;

    // Filter by correctness
    if (showOnlyIncorrect) {
      filtered = filtered.filter((question, index) => {
        const userAnswer = answers[question.id];
        const isAttempted = userAnswer !== null && userAnswer !== undefined && userAnswer !== '' && userAnswer !== 'null' && userAnswer !== 'undefined';
        const isCorrect = isAttempted ? checkAnswerCorrectness(question, userAnswer) : false;
        return !isCorrect && isAttempted;
      });
    }

    if (showOnlyUnattempted) {
      filtered = filtered.filter((question, index) => {
        const userAnswer = answers[question.id];
        return userAnswer === null || userAnswer === undefined || userAnswer === '';
      });
    }

    // Filter by difficulty
    if (filterByDifficulty !== 'all') {
      filtered = filtered.filter(question => 
        (question.difficulty || 'medium').toLowerCase() === filterByDifficulty
      );
    }

    // Filter by topic
    if (filterByTopic !== 'all') {
      filtered = filtered.filter(question => 
        (question.topic || 'General').toLowerCase().includes(filterByTopic.toLowerCase())
      );
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(question => 
        question.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (question.topic || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const filteredQuestions = getFilteredQuestions();
  const currentQuestion = filteredQuestions[currentQuestionIndex];

  // Get unique topics and difficulties for filters
  const topics = [...new Set(questions.map(q => q.topic || 'General'))];
  const difficulties = [...new Set(questions.map(q => q.difficulty || 'medium'))];

  // Check if user answer is correct based on question type
  const checkAnswerCorrectness = (question, userAnswer) => {
    if (userAnswer === null || userAnswer === undefined || userAnswer === '') {
      return false;
    }

    const { type, correctAnswer } = question;

    switch (type) {
      case 'multiple-choice':
        return userAnswer === correctAnswer;
      
      case 'multiple-choice-multi':
        if (Array.isArray(userAnswer) && Array.isArray(correctAnswer)) {
          return userAnswer.length === correctAnswer.length &&
                 userAnswer.every(ans => correctAnswer.includes(ans));
        }
        return false;
      
      case 'integer':
      case 'floating-point':
        return parseFloat(userAnswer) === parseFloat(correctAnswer);
      
      case 'subjective':
        return String(userAnswer).toLowerCase().trim() === String(correctAnswer).toLowerCase().trim();
      
      case 'matrix-match':
        if (Array.isArray(userAnswer) && Array.isArray(correctAnswer)) {
          return JSON.stringify(userAnswer.sort()) === JSON.stringify(correctAnswer.sort());
        }
        return false;
      
      case 'assertion-reason':
        return userAnswer === correctAnswer;
      
      default:
        return userAnswer === correctAnswer;
    }
  };

  // Get question analysis data
  const getQuestionAnalysis = (question, index) => {
    const userAnswer = answers[question.id];
    // Try to find detailedResult by questionId first, then fallback to index
    let detailedResult = detailedResults.find(r => r.questionId === question.id);
    if (!detailedResult && index < detailedResults.length) {
      detailedResult = detailedResults[index];
    }
    
    const isAttempted = userAnswer !== null && userAnswer !== undefined && userAnswer !== '' && userAnswer !== 'null' && userAnswer !== 'undefined';
    const isCorrect = isAttempted ? checkAnswerCorrectness(question, userAnswer) : false;
    
    // Get explanation from multiple sources:
    // 1. Current question object
    // 2. Detailed result (matched by questionId)
    // 3. Original test data (if available) - match by question ID
    let explanation = '';
    
    // Helper function to safely get explanation string
    const getExplanationString = (exp) => {
      if (exp === null || exp === undefined) return '';
      const str = String(exp).trim();
      // Don't treat "null", "undefined", or empty strings as valid
      if (str === '' || str === 'null' || str === 'undefined') return '';
      return str;
    };
    
    // Try question.explanation first
    if (question.explanation) {
      const exp = getExplanationString(question.explanation);
      if (exp) explanation = exp;
    }
    
    // If not found, try detailedResult
    if (!explanation && detailedResult?.explanation) {
      const exp = getExplanationString(detailedResult.explanation);
      if (exp) explanation = exp;
    }
    
    // If still not found and testData is available, try original test data
    if (!explanation && testData) {
      const originalQuestions = testData.questions || testData.originalTestData?.questions || [];
      
      // First try to match by ID
      let originalQuestion = originalQuestions.find(q => q.id === question.id);
      
      // If not found by ID, try to match by index as fallback
      if (!originalQuestion && index < originalQuestions.length) {
        originalQuestion = originalQuestions[index];
      }
      
      if (originalQuestion?.explanation) {
        const exp = getExplanationString(originalQuestion.explanation);
        if (exp) explanation = exp;
      }
    }
    
    return {
      userAnswer: isAttempted ? userAnswer : null,
      isCorrect,
      isAttempted,
      timeSpent: detailedResult?.timeSpent || 0,
      difficulty: question.difficulty || 'medium',
      topic: question.topic || 'General',
      explanation: explanation,
      status: isAttempted ? (isCorrect ? 'correct' : 'incorrect') : 'unattempted'
    };
  };

  const currentAnalysis = currentQuestion ? getQuestionAnalysis(currentQuestion, questions.indexOf(currentQuestion)) : null;

  // Navigation functions
  const goToNext = () => {
    if (currentQuestionIndex < filteredQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const goToPrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const goToQuestion = (index) => {
    setCurrentQuestionIndex(index);
  };

  // Format time
  const formatTime = (seconds) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${minutes}m ${secs}s`;
  };

  // Format answer for display based on question type
  const formatAnswerForDisplay = (question, answer) => {
    if (answer === null || answer === undefined) {
      return 'N/A';
    }

    // For multiple choice questions, convert numeric indices to letters (A, B, C, D)
    if (question.type === 'multiple-choice' || question.type === 'multiple-choice-multi' || !question.type) {
      if (typeof answer === 'number' && question.options && question.options.length > 0) {
        // Check if it's a valid index
        if (answer >= 0 && answer < question.options.length) {
          return String.fromCharCode(65 + answer); // A, B, C, D
        }
      } else if (typeof answer === 'string' && answer.length === 1 && /^[A-Z]$/i.test(answer)) {
        // Already a letter, return as is
        return answer.toUpperCase();
      }
    }

    // For array answers (multiple choice multi, matrix match)
    if (Array.isArray(answer)) {
      if (question.type === 'multiple-choice-multi' && question.options && question.options.length > 0) {
        // Convert array of indices to letters
        return answer.map(idx => {
          if (typeof idx === 'number' && idx >= 0 && idx < question.options.length) {
            return String.fromCharCode(65 + idx);
          }
          return String(idx);
        }).join(', ');
      }
      return answer.join(', ');
    }

    // For integer, floating-point, subjective, and other types, display as-is
    return String(answer);
  };

  // Get performance stats
  const getPerformanceStats = () => {
    const stats = {
      correct: 0,
      incorrect: 0,
      unattempted: 0,
      totalTime: 0,
      byDifficulty: { easy: 0, medium: 0, hard: 0 },
      byTopic: {}
    };

    questions.forEach((question, index) => {
      const analysis = getQuestionAnalysis(question, index);
      stats.totalTime += analysis.timeSpent;
      
      if (analysis.status === 'correct') stats.correct++;
      else if (analysis.status === 'incorrect') stats.incorrect++;
      else stats.unattempted++;

      // By difficulty
      const diff = analysis.difficulty.toLowerCase();
      if (stats.byDifficulty[diff] !== undefined) {
        stats.byDifficulty[diff]++;
      }

      // By topic
      const topic = analysis.topic;
      if (!stats.byTopic[topic]) stats.byTopic[topic] = 0;
      stats.byTopic[topic]++;
    });

    return stats;
  };

  const performanceStats = getPerformanceStats();

  if (!isOpen || !testResult) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full h-full max-w-none flex flex-col relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 flex items-center justify-between p-3 sm:p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
              <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                <div className="p-1.5 sm:p-2 bg-blue-100 dark:bg-blue-900 rounded-lg flex-shrink-0">
                  <BarChart3 className="text-blue-600 dark:text-blue-400 w-4 h-4 sm:w-5 sm:h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-base sm:text-xl font-semibold text-gray-900 dark:text-white truncate">
                    Test Analysis
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">
                    {testName} • {totalQuestions} Questions
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
              <button
                onClick={onClose}
                className="p-1.5 sm:p-2 bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400 rounded-lg"
              >
                <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
            {/* Sidebar - Hidden on mobile */}
            <div className="hidden lg:flex w-80 bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 flex-col overflow-hidden">
              {/* Performance Overview - Compact - Hidden on mobile */}
              <div className="hidden lg:block border-b border-gray-200 dark:border-gray-700">
                {/* Performance Overview Header with Toggle Button */}
                <button
                  onClick={() => setIsPerformanceOverviewExpanded(!isPerformanceOverviewExpanded)}
                  className="w-full p-2 sm:p-3 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <h3 className="font-semibold text-gray-900 dark:text-white text-xs sm:text-sm">Performance Overview</h3>
                  {isPerformanceOverviewExpanded ? (
                    <ChevronUp className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  )}
                </button>
                
                {/* Performance Overview Content - Collapsible */}
                <AnimatePresence>
                  {isPerformanceOverviewExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="p-2 sm:p-3 pt-0">
                        <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
                          <div className="text-center p-1.5 sm:p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                            <div className="text-xs sm:text-sm font-bold text-green-600 dark:text-green-400">{correctAnswers}</div>
                            <div className="text-[10px] sm:text-xs text-green-700 dark:text-green-300">Correct</div>
                          </div>
                          <div className="text-center p-1.5 sm:p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                            <div className="text-xs sm:text-sm font-bold text-red-600 dark:text-red-400">{wrongAnswers}</div>
                            <div className="text-[10px] sm:text-xs text-red-700 dark:text-red-300">Wrong</div>
                          </div>
                          <div className="text-center p-1.5 sm:p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <div className="text-xs sm:text-sm font-bold text-gray-600 dark:text-gray-400">{unattempted}</div>
                            <div className="text-[10px] sm:text-xs text-gray-700 dark:text-gray-300">Unattempted</div>
                          </div>
                          <div className="text-center p-1.5 sm:p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                            <div className="text-xs sm:text-sm font-bold text-blue-600 dark:text-blue-400">{accuracy}%</div>
                            <div className="text-[10px] sm:text-xs text-blue-700 dark:text-blue-300">Accuracy</div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Filters - Compact - Hidden on mobile */}
              <div className="hidden lg:block border-b border-gray-200 dark:border-gray-700">
                {/* Filter Header with Toggle Button */}
                <button
                  onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
                  className="w-full p-2 sm:p-3 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <h3 className="font-semibold text-gray-900 dark:text-white text-xs sm:text-sm">Filters</h3>
                  {isFiltersExpanded ? (
                    <ChevronUp className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  )}
                </button>
                
                {/* Filter Content - Collapsible */}
                <AnimatePresence>
                  {isFiltersExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="p-2 sm:p-3 pt-0">
                        {/* Search */}
                        <div className="relative mb-2">
                          <Search className="w-3 h-3 sm:w-3.5 sm:h-3.5 absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search questions..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-7 sm:pl-8 pr-2 py-1.5 sm:py-2 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                          />
                        </div>

                        {/* Quick Filters */}
                        <div className="space-y-1 mb-2">
                          <button
                            onClick={() => setShowOnlyIncorrect(!showOnlyIncorrect)}
                            className={`w-full text-left px-2 py-1.5 sm:py-2 text-xs rounded touch-manipulation ${
                              showOnlyIncorrect ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                            }`}
                          >
                            Show Only Incorrect
                          </button>
                          <button
                            onClick={() => setShowOnlyUnattempted(!showOnlyUnattempted)}
                            className={`w-full text-left px-2 py-1.5 sm:py-2 text-xs rounded touch-manipulation ${
                              showOnlyUnattempted ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                            }`}
                          >
                            Show Only Unattempted
                          </button>
                        </div>

                        {/* Difficulty Filter */}
                        <div className="mb-2">
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Difficulty</label>
                          <select
                            value={filterByDifficulty}
                            onChange={(e) => setFilterByDifficulty(e.target.value)}
                            className="w-full px-2 py-1.5 sm:py-2 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 touch-manipulation"
                          >
                            <option value="all">All Difficulties</option>
                            {difficulties.map(diff => (
                              <option key={diff} value={diff.toLowerCase()}>
                                {diff.charAt(0).toUpperCase() + diff.slice(1)}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Topic Filter */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Topic</label>
                          <select
                            value={filterByTopic}
                            onChange={(e) => setFilterByTopic(e.target.value)}
                            className="w-full px-2 py-1.5 sm:py-2 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 touch-manipulation"
                          >
                            <option value="all">All Topics</option>
                            {topics.map(topic => (
                              <option key={topic} value={topic}>
                                {topic}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Question List - More Space */}
              <div className="flex-1 overflow-y-auto p-2 sm:p-3">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2 sm:mb-3 text-xs sm:text-sm">
                  Questions ({filteredQuestions.length})
                </h3>
                <div className="space-y-1">
                  {filteredQuestions.map((question, index) => {
                    const analysis = getQuestionAnalysis(question, questions.indexOf(question));
                    const isCurrent = index === currentQuestionIndex;
                    
                    return (
                      <button
                        key={question.id}
                        onClick={() => goToQuestion(index)}
                        className={`w-full text-left p-1.5 sm:p-2 rounded border transition-colors touch-manipulation ${
                          isCurrent 
                            ? 'bg-blue-100 border-blue-300 dark:bg-blue-900 dark:border-blue-700' 
                            : 'bg-white border-gray-200 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:hover:bg-gray-700'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-1.5 sm:space-x-2">
                            <span className="text-[10px] sm:text-xs font-medium text-gray-900 dark:text-white">
                              Q{questions.indexOf(question) + 1}
                            </span>
                            {analysis.status === 'correct' && (
                              <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-green-600" />
                            )}
                            {analysis.status === 'incorrect' && (
                              <XCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-red-600" />
                            )}
                            {analysis.status === 'unattempted' && (
                              <AlertTriangle className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-yellow-600" />
                            )}
                          </div>
                          <div className="flex items-center space-x-1 text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">
                            <Clock className="w-2 h-2 sm:w-2.5 sm:h-2.5" />
                            <span>{formatTime(analysis.timeSpent)}</span>
                          </div>
                        </div>
                        <div className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-300 mt-0.5 sm:mt-1 truncate">
                          {analysis.topic} • {analysis.difficulty}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-y-auto">
              {currentQuestion ? (
                <>
                  {/* Question Header */}
                  <div className="p-2 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                          <span className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white">
                            Question {questions.indexOf(currentQuestion) + 1}
                          </span>
                          {currentAnalysis.status === 'correct' && (
                            <div className="flex items-center space-x-1 text-green-600">
                              <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span className="text-[10px] sm:text-sm font-medium">Correct</span>
                            </div>
                          )}
                          {currentAnalysis.status === 'incorrect' && (
                            <div className="flex items-center space-x-1 text-red-600">
                              <XCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span className="text-[10px] sm:text-sm font-medium">Incorrect</span>
                            </div>
                          )}
                          {currentAnalysis.status === 'unattempted' && (
                            <div className="flex items-center space-x-1 text-yellow-600">
                              <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span className="text-[10px] sm:text-sm font-medium">Unattempted</span>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 sm:gap-4 text-[10px] sm:text-sm text-gray-600 dark:text-gray-300">
                          <div className="flex items-center space-x-1">
                            <Clock className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5" />
                            <span>{formatTime(currentAnalysis.timeSpent)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Target className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5" />
                            <span className="capitalize">{currentAnalysis.difficulty}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <BookOpen className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5" />
                            <span className="truncate">{currentAnalysis.topic}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between sm:justify-end space-x-2 sm:space-x-2 flex-shrink-0">
                        <button
                          onClick={goToPrevious}
                          disabled={currentQuestionIndex === 0}
                          className="p-1.5 sm:p-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                        >
                          <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </button>
                        <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                          {currentQuestionIndex + 1} of {filteredQuestions.length}
                        </span>
                        <button
                          onClick={goToNext}
                          disabled={currentQuestionIndex === filteredQuestions.length - 1}
                          className="p-1.5 sm:p-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                        >
                          <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Question Content */}
                  <div className="flex-1 overflow-y-auto p-2 sm:p-6">
                    <div className="max-w-4xl mx-auto">
                      {/* Question with Images */}
                      <div className="mb-3 sm:mb-6">
                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-2.5 sm:p-6">
                          <div className="prose dark:prose-invert max-w-none text-sm sm:text-base text-gray-900 dark:text-gray-100">
                            <KatexRenderer content={currentQuestion.question} />
                          </div>
                          
                          {/* Question Image */}
                          {currentQuestion.questionImageUrl && (
                            <div className="mt-2 sm:mt-4">
                              <img
                                src={currentQuestion.questionImageUrl}
                                alt="Question"
                                className="max-w-full h-auto max-h-64 sm:max-h-96 rounded-lg border shadow-sm"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Options with Images - Only show for MCQ questions */}
                      {currentQuestion.options && currentQuestion.options.length > 0 && 
                       (currentQuestion.type === 'multiple-choice' || currentQuestion.type === 'multiple-choice-multi' || !currentQuestion.type) && (
                      <div className="mb-3 sm:mb-6">
                        <h4 className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white mb-2 sm:mb-4">Options</h4>
                        <div className="space-y-2 sm:space-y-3">
                          {currentQuestion.options.map((option, index) => {
                            const optionKey = String.fromCharCode(65 + index); // A, B, C, D
                            
                            // Fix: Handle both numeric and string answer formats
                            let isUserAnswer = false;
                            let isCorrectAnswer = false;
                            
                            // Check if user answer matches this option (handle both index and letter formats)
                            if (currentAnalysis.userAnswer !== null && currentAnalysis.userAnswer !== undefined) {
                              if (typeof currentAnalysis.userAnswer === 'number') {
                                isUserAnswer = currentAnalysis.userAnswer === index;
                              } else if (typeof currentAnalysis.userAnswer === 'string') {
                                isUserAnswer = currentAnalysis.userAnswer === optionKey;
                              }
                            }
                            
                            // Check if this is the correct answer (handle both index and letter formats)
                            if (currentQuestion.correctAnswer !== null && currentQuestion.correctAnswer !== undefined) {
                              if (typeof currentQuestion.correctAnswer === 'number') {
                                isCorrectAnswer = currentQuestion.correctAnswer === index;
                              } else if (typeof currentQuestion.correctAnswer === 'string') {
                                isCorrectAnswer = currentQuestion.correctAnswer === optionKey;
                              }
                            }
                            
                            let optionClass = "p-4 border-2 rounded-lg transition-all duration-200 ";
                            if (isCorrectAnswer) {
                              optionClass += "border-green-500 bg-green-100 dark:bg-green-900/30 dark:border-green-400 shadow-lg shadow-green-200 dark:shadow-green-900/20";
                            } else if (isUserAnswer && !isCorrectAnswer) {
                              optionClass += "border-red-500 bg-red-100 dark:bg-red-900/30 dark:border-red-400 shadow-lg shadow-red-200 dark:shadow-red-900/20";
                            } else {
                              optionClass += "border-gray-200 bg-gray-50 dark:bg-gray-800 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500";
                            }

                            return (
                              <div key={index} className={optionClass}>
                                <div className="flex items-start space-x-2 sm:space-x-3">
                                  <div className={`w-5 h-5 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-[10px] sm:text-sm font-bold flex-shrink-0 ${
                                    isCorrectAnswer 
                                      ? 'bg-green-500 text-white shadow-lg shadow-green-300 dark:shadow-green-600' 
                                      : isUserAnswer && !isCorrectAnswer
                                      ? 'bg-red-500 text-white shadow-lg shadow-red-300 dark:shadow-red-600'
                                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                  }`}>
                                    {optionKey}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="prose dark:prose-invert max-w-none text-xs sm:text-base text-gray-900 dark:text-gray-100 break-words">
                                      <KatexRenderer content={option} />
                                    </div>
                                    
                                    {/* Option Image */}
                                    {currentQuestion.optionImageUrls && currentQuestion.optionImageUrls[index] && (
                                      <div className="mt-1.5 sm:mt-2">
                                        <img
                                          src={currentQuestion.optionImageUrls[index]}
                                          alt={`Option ${optionKey}`}
                                          className="max-w-full h-auto max-h-20 sm:max-h-32 rounded border"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                      </div>
                                    )}
                                    
                                    {isUserAnswer && (
                                      <div className="mt-1 sm:mt-2 text-[10px] sm:text-sm font-medium text-blue-600 dark:text-blue-400">
                                        Your Answer
                                      </div>
                                    )}
                                    {isCorrectAnswer && (
                                      <div className="mt-1 sm:mt-2 text-[10px] sm:text-sm font-medium text-green-600 dark:text-green-400">
                                        Correct Answer
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      )}

                      {/* Answer Summary */}
                      <div className="mb-3 sm:mb-6">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
                          <div className="bg-blue-50 dark:bg-blue-900/20 p-2 sm:p-4 rounded-lg">
                            <div className="text-[10px] sm:text-sm text-blue-700 dark:text-blue-300 mb-0.5 sm:mb-1">Your Answer</div>
                            <div className="text-sm sm:text-lg font-semibold text-blue-900 dark:text-blue-100">
                              {currentAnalysis.userAnswer !== null && currentAnalysis.userAnswer !== undefined 
                                ? formatAnswerForDisplay(currentQuestion, currentAnalysis.userAnswer)
                                : 'Not Attempted'}
                            </div>
                          </div>
                          <div className="bg-green-50 dark:bg-green-900/20 p-2 sm:p-4 rounded-lg">
                            <div className="text-[10px] sm:text-sm text-green-700 dark:text-green-300 mb-0.5 sm:mb-1">Correct Answer</div>
                            <div className="text-sm sm:text-lg font-semibold text-green-900 dark:text-green-100">
                              {currentQuestion.correctAnswer !== null && currentQuestion.correctAnswer !== undefined
                                ? formatAnswerForDisplay(currentQuestion, currentQuestion.correctAnswer)
                                : 'N/A'}
                            </div>
                          </div>
                          <div className="bg-purple-50 dark:bg-purple-900/20 p-2 sm:p-4 rounded-lg">
                            <div className="text-[10px] sm:text-sm text-purple-700 dark:text-purple-300 mb-0.5 sm:mb-1">Time Spent</div>
                            <div className="text-sm sm:text-lg font-semibold text-purple-900 dark:text-purple-100">
                              {formatTime(currentAnalysis.timeSpent)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Explanation Section - Always Show */}
                      {showExplanations && (() => {
                        // Helper to extract valid explanation text
                        const extractExplanation = (exp) => {
                          if (exp === null || exp === undefined) return null;
                          const str = String(exp).trim();
                          if (str === '' || str === 'null' || str === 'undefined') return null;
                          return str;
                        };

                        let explanationText = null;
                        const questionIndex = questions.indexOf(currentQuestion);

                        // Priority 1: Check currentQuestion.explanation directly
                        if (currentQuestion?.explanation) {
                          explanationText = extractExplanation(currentQuestion.explanation);
                        }

                        // Priority 2: Check detailedResults
                        if (!explanationText && detailedResults.length > 0) {
                          let detailedResult = detailedResults.find(r => r.questionId === currentQuestion.id);
                          if (!detailedResult && questionIndex >= 0 && questionIndex < detailedResults.length) {
                            detailedResult = detailedResults[questionIndex];
                          }
                          if (detailedResult?.explanation) {
                            explanationText = extractExplanation(detailedResult.explanation);
                          }
                        }

                        // Priority 3: Check currentAnalysis (from getQuestionAnalysis)
                        if (!explanationText && currentAnalysis?.explanation) {
                          explanationText = extractExplanation(currentAnalysis.explanation);
                        }

                        // Priority 4: Check testData original questions
                        if (!explanationText && testData) {
                          const originalQuestions = testData.questions || testData.originalTestData?.questions || [];
                          let originalQuestion = originalQuestions.find(q => q.id === currentQuestion.id);
                          if (!originalQuestion && questionIndex >= 0 && questionIndex < originalQuestions.length) {
                            originalQuestion = originalQuestions[questionIndex];
                          }
                          if (originalQuestion?.explanation) {
                            explanationText = extractExplanation(originalQuestion.explanation);
                          }
                        }

                        // Always display explanation section
                        return (
                          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-2.5 sm:p-6 mt-3 sm:mt-6">
                            <h4 className="text-sm sm:text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2 sm:mb-3 flex items-center">
                              <BookOpen className="w-3.5 h-3.5 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
                              Explanation
                            </h4>
                            {explanationText ? (
                              <div className="prose dark:prose-invert max-w-none text-xs sm:text-base text-gray-900 dark:text-gray-100 break-words">
                                <KatexRenderer content={explanationText} />
                              </div>
                            ) : (
                              <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 italic">
                                No explanation available for this question.
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <AlertTriangle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Questions Found</h3>
                    <p className="text-gray-600 dark:text-gray-400">Try adjusting your filters to see questions.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TestAnalysisModal;
